import { Controller } from "@hotwired/stimulus"

// Wraps a hidden <input type="file"> with a visible drop zone that supports
// three input methods:
//
//   1. Click — opens the native file picker.
//   2. Drag & drop — files dragged from the desktop are attached.
//   3. Paste — Cmd/Ctrl+V while focus is anywhere inside the surrounding form
//      pastes images from the clipboard (e.g. screenshots).
//
// Newly added images are appended to the input's existing FileList using the
// DataTransfer API so submitting the form delivers them all to the server in
// one go, and each one shows as a thumbnail with a per-file remove button so
// users can correct mistakes before submitting.
export default class extends Controller {
  static targets = ["input", "dropzone", "previews"]

  connect() {
    this._activeClasses = ["border-blue-400", "bg-blue-900/20"]
    this._dragCounter = 0
    this._form = this.element.closest("form")

    this._onDragEnter = (e) => {
      if (!this._hasFiles(e)) return
      e.preventDefault()
      this._dragCounter++
      this.dropzoneTarget.classList.add(...this._activeClasses)
    }
    this._onDragOver = (e) => {
      if (!this._hasFiles(e)) return
      e.preventDefault()
    }
    this._onDragLeave = () => {
      this._dragCounter--
      if (this._dragCounter <= 0) {
        this._dragCounter = 0
        this.dropzoneTarget.classList.remove(...this._activeClasses)
      }
    }
    this._onDrop = (e) => {
      if (!this._hasFiles(e)) return
      e.preventDefault()
      this._dragCounter = 0
      this.dropzoneTarget.classList.remove(...this._activeClasses)
      this._addFiles(Array.from(e.dataTransfer.files))
    }

    this.dropzoneTarget.addEventListener("dragenter", this._onDragEnter)
    this.dropzoneTarget.addEventListener("dragover", this._onDragOver)
    this.dropzoneTarget.addEventListener("dragleave", this._onDragLeave)
    this.dropzoneTarget.addEventListener("drop", this._onDrop)

    // Paste handler is registered on the document so it works regardless of
    // exactly which form field has focus, but we only attach files when the
    // active element is inside our form (or the dropzone itself) so pastes
    // into other forms / Trix editors / the rest of the page aren't hijacked.
    this._onPaste = (e) => {
      const active = document.activeElement
      const inScope = (this._form && this._form.contains(active)) || this.element.contains(active)
      if (!inScope) return
      const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith("image/"))
      if (files.length === 0) return
      e.preventDefault()
      this._addFiles(files)
    }
    document.addEventListener("paste", this._onPaste)

    this._onChange = () => this._renderPreviews()
    this.inputTarget.addEventListener("change", this._onChange)

    if (this._form) {
      this._onReset = () => {
        // form.reset() clears the input's selected files; re-render after the
        // browser processes the reset so previews/thumbnails stay in sync.
        setTimeout(() => this._renderPreviews(), 0)
      }
      this._form.addEventListener("reset", this._onReset)
    }

    this._renderPreviews()
  }

  disconnect() {
    this.dropzoneTarget.removeEventListener("dragenter", this._onDragEnter)
    this.dropzoneTarget.removeEventListener("dragover", this._onDragOver)
    this.dropzoneTarget.removeEventListener("dragleave", this._onDragLeave)
    this.dropzoneTarget.removeEventListener("drop", this._onDrop)
    document.removeEventListener("paste", this._onPaste)
    this.inputTarget.removeEventListener("change", this._onChange)
    if (this._form && this._onReset) this._form.removeEventListener("reset", this._onReset)
    this._revokePreviewUrls()
  }

  open() {
    this.inputTarget.click()
  }

  removeFile(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const dt = new DataTransfer()
    Array.from(this.inputTarget.files).forEach((f, i) => {
      if (i !== idx) dt.items.add(f)
    })
    this.inputTarget.files = dt.files
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this._renderPreviews()
  }

  _hasFiles(e) {
    return e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")
  }

  _addFiles(files) {
    const images = files.filter(f => f.type.startsWith("image/"))
    if (images.length === 0) return
    const dt = new DataTransfer()
    const multiple = this.inputTarget.multiple
    if (multiple) {
      Array.from(this.inputTarget.files).forEach(f => dt.items.add(f))
      images.forEach(f => dt.items.add(f))
    } else {
      // Single-file input: replace any prior selection with the latest pick.
      dt.items.add(images[images.length - 1])
    }
    this.inputTarget.files = dt.files
    // Setting `.files` programmatically does NOT fire `change` automatically,
    // so dirty-state listeners (e.g. the inline-editor controller's Save
    // button) wouldn't otherwise know that the field's value just changed.
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this._renderPreviews()
  }

  _renderPreviews() {
    if (!this.hasPreviewsTarget) return
    this._revokePreviewUrls()
    this.previewsTarget.innerHTML = ""
    const files = Array.from(this.inputTarget.files || [])
    if (files.length === 0) {
      this.previewsTarget.classList.add("hidden")
      return
    }
    this.previewsTarget.classList.remove("hidden")
    this._previewUrls = []
    files.forEach((file, idx) => {
      const url = URL.createObjectURL(file)
      this._previewUrls.push(url)

      const item = document.createElement("div")
      item.className = "relative inline-flex group"

      const img = document.createElement("img")
      img.src = url
      img.alt = file.name
      img.className = "w-16 h-16 rounded object-cover border border-gray-700"

      const remove = document.createElement("button")
      remove.type = "button"
      remove.title = "Remove"
      remove.dataset.action = "click->image-uploader#removeFile"
      remove.dataset.index = String(idx)
      remove.className =
        "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs leading-none flex items-center justify-center cursor-pointer shadow"
      remove.innerHTML = "&times;"

      item.appendChild(img)
      item.appendChild(remove)
      this.previewsTarget.appendChild(item)
    })
  }

  _revokePreviewUrls() {
    if (!this._previewUrls) return
    this._previewUrls.forEach(url => URL.revokeObjectURL(url))
    this._previewUrls = []
  }
}
